import React, { useMemo, useCallback, useState } from 'react';
import propTypes from "prop-types"
import { AgGridReact } from 'ag-grid-react'; // the AG Grid React Component
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; //
import { LicenseManager } from "ag-grid-enterprise";
import { Search } from '../Search';
import { Tooltip } from '../Tooltip';
import { FilterIcon } from '../../../theme/icons';
import { Button } from '..';
import CustomNoRowsOverlay from './CustomNoRowsOverlay ';

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENCE_KEY);

export const AGGridTable = React.memo(({ headers, placeholderText, customComponent, tableHeight, rowData, sideNavComponent,
  searchText, onChangeSearch, search, footerComponents, gridOptions, filterParams, renderInpopup, hideCustomDiv, sidebar, tableTitle,
  tabSubTitle, customComponentRight, leftTitleDivClassName, rightTitleDivClassName, activeFilter, ...rest }) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const sideBar = useMemo(() => {
    return {
      toolPanels: [

        {
          id: 'filters',
          labelDefault: 'Search',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        }

      ],
      defaultToolPanel: 'filters',
    };
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      sortable: true,
      resizable: false,
      filter: true,
      flex: 1,
      minWidth: 100,
      minHeight: 64,
      tooltipComponent: Tooltip,
      headerHeight: 100
    };
  }, []);

  const getMainMenuItems = useCallback((params) => {
    const parameters = Object.keys(filterParams);
    switch (params.column.getId()) {
      case parameters?.find((e) => e === (params.column.getId())):
        return filterParams[params.column.getId()];
      default:
        return params.defaultItems;
    }
  }, [filterParams]);


  const loadingCellRenderer = (params) => {
    const { rowIndex, api } = params;
    const { displayedColumns } = api?.columnModel || { displayedColumns: [] }
    return (
      <div role="status" key={rowIndex} className="p-4 space-y-4 w-full border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse">
        <div className="flex items-center justify-between">
          {!!displayedColumns?.length && displayedColumns?.filter(header => !header?.userProvidedColDef?.hide)?.map((header, i) =>
            <div key={header.colId} className={`w-full m-auto flex flex-col justify-center ${i === 0 ? "items-start" : i === displayedColumns?.length - 1 ? "items-end" : "items-center"}`}>
              <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-32 mb-2.5"></div>
              <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>  </div>)}
        </div>
        <span className="sr-only">Loading...</span>
      </div>);
  }

  const FilterButton = () => <Button title="Filter" text="" className={`relative hover:bg-primary-50 rounded-full p-2 flex justify-center items-center ${sidebarVisible ? " " : "bg-white"}`} onClick={() => setSidebarVisible(!sidebarVisible)}
    variant="secondary" icon={
      <>
        <FilterIcon className={`w-4 hover:stroke-primary-300 h-4 min-w-4 !m-auto  `} />
          {sidebarVisible && <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>}
      </>
    } />

  const TabSubtitleComponent = () => {
    return (<div className="text-sm font-semibold text-secondary-300 mr-auto">{tabSubTitle}</div>)
  }

  const noRowsOverlayComponentParams = useMemo(() => {
    return {
      noRowsMessageFunc: () => 'No results found'
    };
  }, []);

  return (
    <div className="w-full">
      {!hideCustomDiv && <div className="px-5 py-6">
        <div className={`flex flex-col lg:flex-row ${renderInpopup ? "justify-end" : "justify-end gap-3"}`}>
          <div className={`flex flex-row items-center justify-end lg:justify-start ${leftTitleDivClassName} `}>
            {tableTitle && <div className="text-lg font-semibold text-secondary-500 lg:mr-auto">{tableTitle}</div>}
            {tabSubTitle && <TabSubtitleComponent />}
          </div>
          <div className={`flex flex-row justify-end  items-end gap-2 ${rightTitleDivClassName}`}>
            {activeFilter && <>{activeFilter}</>}
            {search && <Search onChangeSearch={onChangeSearch} searchText={searchText} placeholderText={placeholderText} />}
            {customComponent && <div className="flex align-center">{customComponent}</div>}
            {sidebar && <FilterButton />}
            {customComponentRight && <div className="flex align-center">{customComponentRight}</div>}
          </div>
        </div>
      </div>}
      <div className={`grid grid-cols-12 gap-2`}>
        <div className={`col-span-12 ${sideNavComponent ? 'lg:col-span-9' : 'md:col-span-12'}`}>
          <div className={`ag-theme-alpine ${renderInpopup ? "h-64" : tableHeight} overflow-auto w-full`}>
            <AgGridReact
              defaultColDef={defaultColDef}
              columnDefs={headers}
              gridOptions={{ ...gridOptions }}
              animateRows={true}
              rowData={rowData}
              rowSelection={'multiple'}
              suppressRowClickSelection={true}
              tooltipShowDelay={0}
              noRowsOverlayComponent={CustomNoRowsOverlay}
              noRowsOverlayComponentParams={noRowsOverlayComponentParams}
              suppressContextMenu={true}
              sideBar={sidebarVisible && sideBar}
              suppressCellFocus={true}
              getMainMenuItems={getMainMenuItems}
              loadingCellRenderer={loadingCellRenderer}
              {...rest}

            ></AgGridReact>
            {footerComponents && <div className="fixed w-[85%] bottom-0 right-0 z-10 p-4 pr-8 bg-white  md:flex md:items-center md:justify-end md:p-">
              {footerComponents}
            </div>}
          </div>
        </div>
        {sideNavComponent && <div className={`col-span-12 lg:col-span-3`}>
          {sideNavComponent}
        </div>}
      </div>
    </div>
  );
});

AGGridTable.propTypes = {
  headers: propTypes.array,
  rowData: propTypes.array,
  placeholderText: propTypes.string,
  customComponent: propTypes.element,
  customComponentRight: propTypes.element,
  searchText: propTypes.string,
  onChangeSearch: propTypes.func,
  search: propTypes.bool,
  footerComponents: propTypes.element,
  gridOptions: propTypes.object,
  filterParams: propTypes.object,
  renderInpopup: propTypes.bool,
  hideCustomDiv: propTypes.bool,
  sidebar: propTypes.bool,
  tableTitle: propTypes.oneOfType([
    propTypes.string,
    propTypes.element
  ]),
  tableHeight: propTypes.string,
  tabSubTitle: propTypes.oneOfType([
    propTypes.string,
    propTypes.element
  ]),
  sideNavComponent: propTypes.any,
  leftTitleDivClassName: propTypes.string,
  rightTitleDivClassName: propTypes.string,
  activeFilter: propTypes.any
}

AGGridTable.defaultProps = {
  headers: [{
    field: "id"
  },
  {
    field: "title"
  },
  ],
  rowData: [{
    id: 23,
    title: "Sample data"
  }],
  placeholderText: "Search",
  searchText: "",
  customComponent: <div></div>,
  customComponentRight: <></>,
  onChangeSearch: () => null,
  search: false,
  footerComponents: null,
  gridOptions: {},
  filterParams: {},
  renderInpopup: false,
  tableHeight: "h-[100vh-256px]",
  hideCustomDiv: false,
  sidebar: false,
  tableTitle: "",
  tabSubTitle: "",
  sideNavComponent: "",
  leftTitleDivClassName: "lg:w-2/4 w-full",
  rightTitleDivClassName: "lg:w-2/4 w-full",
  activeFilter: <></>
}